import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { AccessControlContext } from "../contexts/AccessControlContext";

import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { usePage } from "../../routes";
import { useCancelWorkOrder } from "../data/Workorders/useWorkorderCancel";
import { useUpdateWorkOrder } from "../data/Workorders/useWorkorderUpdate";
import { useWorkorderWithAssignmentById } from "../data/Workorders/useWorkorderWithAssignmentById";

import ControlPointDuplicateIcon from "@mui/icons-material/ControlPointDuplicate";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import { Box, Button, Stack } from "@mui/material";

import { Col, Container, Row } from "react-bootstrap";
import { Card } from "../shared/Card";

import { FormContainer, FormItem } from "../shared/FormLayout/FormContainer";
import { MessageCard, MessageType } from "../shared/MessageCard";
import { ProgressButton } from "../shared/ProgressButton";
import { Snackbar } from "../shared/Snackbar";

import { Statuses } from "../shared/Enums";
import { Customer } from "../shared/interfaces/Customer";
import { WorkflowV1 } from "../shared/interfaces/WorkflowV1";
import { WorkOrder, WorkOrderWithAssignment } from "../shared/interfaces/WorkOrder";

import { ExtraDetails, KeyValue } from "../ExtraDetails";
import { ConfirmationDialog } from "../shared/ConfirmationDialog";
import { MessageToast, ToastMode } from "../shared/MessageToast";

import { JsonTryParse, ParsedResult } from "../shared/helpers/JsonParse";
import RouteBreadcrumbs from "../shared/RouteBreadcrumbs";

import { ResultViewer } from "../results/ResultViewer";

import { useAssetsBySubstation } from "../data/Assets/useAssetsBySubstation";
import { CancelButton } from "../shared/CancelButton";
import { Asset } from "../shared/interfaces/Asset";
import { WorkflowV2 } from "../shared/interfaces/WorkflowV2";
import { CompleteWorkorderDialog } from "./CompleteWorkorderDialog";
import WorkorderAssignment from "./WorkorderComponents/WorkorderAssignment";
import WorkorderDetails from "./WorkorderComponents/WorkorderDetails";
import WorkorderLocationAndWorkflow from "./WorkorderComponents/WorkorderLocationAndWorkflow";
import WorkorderStatusChangeButton from "./WorkorderComponents/WorkorderStatusChangeButton";
import { WorkorderCreate } from "./WorkorderCreate";

function getWorkOrderAssetIds(workOrder: WorkOrderWithAssignment | undefined) {
  return workOrder
    ? [
        workOrder.assetId,
        ...(workOrder.workflowDetail?.assets
          ?.map((asset) => asset.assetid)
          .filter((id): id is number => id !== undefined) || []),
      ].filter((id) => id !== 0)
    : [];
}

export function WorkorderViewer() {
  const { t } = useTranslation();

  const workorderViewerPagePath = usePage(WorkorderViewer.name)?.path;
  const navigate = useNavigate();

  //get the workorderID from the URL
  const { workorderId } = useParams<{ workorderId?: string }>();

  const formMethods = useForm();

  const retrievedWorkorder = useWorkorderWithAssignmentById(workorderId);

  const [workorder, setWorkorder] = useState<WorkOrderWithAssignment | undefined>();

  const { data: assetsForSubstation } = useAssetsBySubstation(workorder?.substationId);

  const [editingWorkorder, setEditingWorkorder] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(false);
  const [updateWorkorderLoading, setUpdateWorkorderLoading] = useState(false);
  const [updateWorkorderError, setUpdateWorkorderError] = useState(false);
  const [updateAssignmentError, setUpdateAssignmentError] = useState(false);
  const [unassignmentError, setUnassignmentError] = useState(false);
  const [switchgearErrorToastMessage, setSwitchgearErrorToastMessage] = useState<string | undefined>(undefined);

  const accessControlContext = useContext(AccessControlContext);
  const userCanViewWorkorder = workorder && accessControlContext.canI("view", "workorder", workorder.customerId);
  const userCanEditWorkorder = workorder && accessControlContext.canI("edit", "workorder", workorder.customerId);

  const [pageLoading, setPageLoading] = useState(retrievedWorkorder.isLoading);

  const [confirmCancellation, setConfirmCancellation] = useState(false);
  const [workorderCancelled, setWorkorderCancelled] = useState(false);
  const [resultDialogOpen, setResultDialogOpen] = useState(false);
  const [createWorkorderDialogOpen, setCreateWorkorderDialogOpen] = useState(false);

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarText, setSnackbarText] = useState("");
  const [snackbarAction, setSnackbarAction] = useState<any | undefined>();

  const workorderResultPath = usePage(ResultViewer.name)?.path;

  const {
    mutate: updateWorkorder,
    isError: errorUpdatingWorkorder,
    isLoading: workorderUpdateLoading,
    reset: resetWorkOrderUpdatedStatus,
  } = useUpdateWorkOrder({ onSuccess: onUpdateWorkorderSuccess });

  const {
    mutate: cancelWorkorder,
    isError: errorCancellingWorkorder,
    isLoading: cancellingWorkorderLoading,
    reset: resetCancelWorkOrderStatus,
  } = useCancelWorkOrder({ onSuccess: onCancelWorkorderSuccess });

  useEffect(() => {
    setPageLoading(retrievedWorkorder.isLoading);
  }, [retrievedWorkorder.isLoading]);

  useEffect(() => {
    setWorkorder(retrievedWorkorder.data);
  }, [retrievedWorkorder.data]);

  const onWorkorderUpdated = useCallback(
    (workorderId: number) => {
      setSnackbarText(t("workorders.workorderUpdated", { val: workorderId }));
      setSnackbarAction(null);
      setShowSnackbar(true);
    },
    [t],
  );

  const [selectedAssetIds, setSelectedAssetIds] = useState<number[]>([]);

  useEffect(() => {
    const workOrderAssetIds = getWorkOrderAssetIds(retrievedWorkorder.data);
    setSelectedAssetIds(workOrderAssetIds);
  }, [retrievedWorkorder.data, retrievedWorkorder.isRefetching]);

  const selectedSubstationChildAssets = useMemo(() => {
    if (!assetsForSubstation || !selectedAssetIds) {
      return [];
    }

    return assetsForSubstation.filter((asset) => selectedAssetIds.includes(asset.id));
  }, [assetsForSubstation, selectedAssetIds]);

  const RemoveWorkOrderAssignmentAndAddSelectedAssets = useCallback(
    (workOrderWithAssignment: WorkOrderWithAssignment) => {
      const { assignment, ...workOrder } = workOrderWithAssignment;
      return { ...workOrder, assetIds: selectedAssetIds } as WorkOrder;
    },
    [selectedAssetIds],
  );

  const saveWorkorderUpdates = formMethods.handleSubmit(() => {
    setSwitchgearErrorToastMessage(undefined);
    setUpdateWorkorderLoading(true);
    editingWorkorder &&
      userCanEditWorkorder &&
      workorder &&
      updateWorkorder(RemoveWorkOrderAssignmentAndAddSelectedAssets(workorder));
    setUpdateWorkorderLoading(false);
  });

  function handleResultUploadSuccess() {
    setResultDialogOpen(false);
    setSnackbarText(`${t("results.resultUploadSuccess")} ${t("resultResubmission.resubmittedIfError")}`);
    setShowSnackbar(true);
    retrievedWorkorder.refetch();
  }

  function handleResultUploadCancel() {
    setResultDialogOpen(false);
  }

  useEffect(() => {
    setUpdateWorkorderError(errorUpdatingWorkorder || errorCancellingWorkorder);
  }, [errorCancellingWorkorder, errorUpdatingWorkorder]);

  useEffect(() => {
    setUpdateWorkorderLoading(workorderUpdateLoading || cancellingWorkorderLoading);
  }, [cancellingWorkorderLoading, workorderUpdateLoading]);

  const cancelWorkOrder = useCallback(() => {
    setUpdateWorkorderLoading(true);

    userCanEditWorkorder && workorder && cancelWorkorder(workorder.id);
  }, [cancelWorkorder, userCanEditWorkorder, workorder]);

  function onUpdateWorkorderSuccess() {
    workorder && onWorkorderUpdated(workorder.id);
    setEditingWorkorder(false);
    resetWorkOrderUpdatedStatus();
    retrievedWorkorder.refetch();
  }

  function onCancelWorkorderSuccess() {
    workorder && onWorkorderUpdated(workorder?.id);
    setEditingWorkorder(false);
    setWorkorderCancelled(true);
    resetCancelWorkOrderStatus();
    workorder && setWorkorder({ ...workorder, status: Statuses.CANCELLED });
  }

  function handleEditingAssignmentModeChange(editing: boolean) {
    setEditingAssignment(editing);
  }

  function handleAssignmentUpdateLoading() {
    setUnassignmentError(false);
  }

  function handleAssignmentUpdateSuccess() {
    retrievedWorkorder.refetch();
  }

  function handleAssignmentUpdateError() {
    setUnassignmentError(true);
  }

  function handleSaveButtonClick() {
    saveWorkorderUpdates();
  }

  function handleCancellationConfirmation() {
    cancelWorkOrder();
  }

  function cancelWorkorderCancellation() {
    setConfirmCancellation(false);
  }

  function handleCancelButtonClick() {
    formMethods.reset();
    setEditingWorkorder(false);
    retrievedWorkorder.refetch();
    setWorkorder(retrievedWorkorder.data);
  }

  function handleCompleteButtonClick() {
    if (workorder) {
      setResultDialogOpen(true);
    }
  }

  function handleCancelWorkorderButtonClick() {
    setConfirmCancellation(true);
  }

  function handleEditButtonClick() {
    if (!workorderCancelled && workorder?.status === Statuses.READY) {
      setEditingWorkorder(true);
    }
  }

  function handleDuplicateWorkorderButtonClick() {
    setCreateWorkorderDialogOpen(true);
  }

  function handleDuplicatWorkorderDialogClose() {
    setCreateWorkorderDialogOpen(false);
  }

  function handleDuplicateWorkorderCreated(workorderId: number) {
    setSnackbarText(t("workorders.workorderCreated", { val: workorderId }));
    setShowSnackbar(true);
    setCreateWorkorderDialogOpen(false);

    workorderViewerPagePath && navigate(workorderViewerPagePath.replace(":workorderId", workorderId.toString()));
  }

  function handleInputChange<KeyType extends keyof WorkOrderWithAssignment>(
    fieldName: KeyType,
    value: WorkOrderWithAssignment[KeyType] | undefined,
  ) {
    setWorkorder((prevState) => prevState && { ...prevState, [fieldName]: value });
  }

  function handleSubstationChange(substationId?: number) {
    workorder &&
      substationId &&
      setWorkorder({
        ...workorder,
        substationId: substationId,
        assetIds: undefined,
        workflowId: undefined,
      });
  }

  function handleSwitchgearChange(assets?: Asset[]) {
    setSelectedAssetIds(assets ? assets.map((asset) => asset.id) : []);
    workorder &&
      setWorkorder({
        ...workorder,
        workflowId: undefined,
      });
  }

  function handleWorkflowChange(workflow: WorkflowV1 | WorkflowV2) {
    workorder &&
      setWorkorder({
        ...workorder,
        workflowId: workflow.id,
        workflowDetail: workflow.workflowDetail,
        workflowRevisionNo: workflow.revisionNo,
      });
  }

  function handleExtraDetailsChange(keyValuePairs: KeyValue[]) {
    if (workorder) setWorkorder({ ...workorder, extraDetail: keyValuePairs });
  }

  const clientDetailsResult: ParsedResult | undefined = workorder && JsonTryParse(workorder.clientDetail);
  const clientDetailsList: FormItem[] | undefined = clientDetailsResult?.parsed?.map((keyValuePair: any) => {
    return {
      key: keyValuePair.key,
      label: keyValuePair.key,
      value: keyValuePair.value,
    };
  });

  if (retrievedWorkorder.error) {
    return (
      <div className="w-100 d-flex justify-content-center pt-2">
        <MessageToast
          text={t("common.dataFetchError", { val: "$t(workorders.workorder)" })}
          mode={ToastMode.Error}
          show
        />
      </div>
    );
  } else if (userCanViewWorkorder === false) {
    return (
      <div className="w-100 d-flex justify-content-center pt-2">
        <MessageCard
          messageType={MessageType.Warning}
          title={t("common.operationForbiddenError", {
            operation: t("common.view"),
            item: "$t(workorders.workorder)",
          })}
          secondaryText={t("common.requestAccess")}
        />
      </div>
    );
  } else
    return (
      <React.Fragment>
        <FormProvider {...formMethods}>
          <Container fluid>
            <Row>
              <Col className="pb-3">
                <Box display="flex" flexDirection="row" justifyContent="space-between">
                  <RouteBreadcrumbs
                    customPageTitle={
                      pageLoading || !workorder
                        ? t("workorders.workorder")
                        : `${t("workorders.workorder")} - ${workorder.id || ""}`
                    }
                  />
                  {workorder && userCanEditWorkorder && !editingAssignment && !workorderCancelled && (
                    <Stack direction="row" justifyContent="flex-end" spacing={1} my={0.5}>
                      {!editingWorkorder && (
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<ControlPointDuplicateIcon />}
                          onClick={handleDuplicateWorkorderButtonClick}
                        >
                          {t("workorders.duplicateWorkorder")}
                        </Button>
                      )}

                      {workorder.status === Statuses.READY && (
                        <>
                          {editingWorkorder ? (
                            <React.Fragment>
                              <ProgressButton
                                startIcon={<SaveIcon />}
                                onClick={handleSaveButtonClick}
                                disabled={updateWorkorderLoading}
                                loading={updateWorkorderLoading}
                              >
                                {" "}
                                {t("common.save")}{" "}
                              </ProgressButton>
                              <CancelButton onClick={handleCancelButtonClick} />
                            </React.Fragment>
                          ) : (
                            <Button
                              variant="contained"
                              color="primary"
                              disabled={workorderCancelled}
                              startIcon={<EditIcon />}
                              onClick={handleEditButtonClick}
                            >
                              {t("common.edit")}{" "}
                            </Button>
                          )}
                        </>
                      )}

                      {!editingWorkorder &&
                        (workorder.status === Statuses.READY || workorder.status === Statuses.ASSIGNED) && (
                          <WorkorderStatusChangeButton
                            status={workorder.status}
                            loading={updateWorkorderLoading}
                            onWorkorderCancelClick={handleCancelWorkorderButtonClick}
                            onWorkorderCompleteClick={handleCompleteButtonClick}
                            customerId={workorder.customerId}
                          />
                        )}

                      {workorder.status === Statuses.COMPLETE && workorder.captureId && workorder.assetIds && (
                        <span className="pt-1">
                          <React.Fragment>
                            <Button
                              variant="contained"
                              color="primary"
                              component={Link}
                              to={{
                                pathname: workorderResultPath
                                  ?.replace(":resultType", "byHVSubstation")
                                  .replace(":captureId", workorder.captureId.toString())
                                  .replace(":switchgearAssetId", workorder.substationId.toString()),
                              }}
                            >
                              {t("results.viewResults")}
                            </Button>
                          </React.Fragment>
                        </span>
                      )}
                    </Stack>
                  )}
                </Box>
              </Col>
            </Row>

            {workorder && (
              <Row>
                <Col lg={6}>
                  <WorkorderDetails
                    workorder={workorder}
                    editingWorkorder={editingWorkorder}
                    isLoading={pageLoading}
                    onFieldChange={handleInputChange}
                  />
                </Col>

                <Col lg={6}>
                  <WorkorderLocationAndWorkflow
                    workorder={workorder}
                    editingWorkorder={editingWorkorder}
                    isLoading={pageLoading}
                    substationChildAssets={assetsForSubstation}
                    selectedSubstationChildAssets={selectedSubstationChildAssets}
                    onSubstationChange={handleSubstationChange}
                    onAssetsChange={handleSwitchgearChange}
                    onWorkflowChange={handleWorkflowChange}
                  />

                  <WorkorderAssignment
                    workorder={workorder}
                    editingWorkorder={editingWorkorder}
                    isLoading={pageLoading}
                    editingAssignment={editingAssignment}
                    onEditingAssignmentModeChange={handleEditingAssignmentModeChange}
                    onAssignmentUpdateSuccess={handleAssignmentUpdateSuccess}
                    onAssignmentUpdateError={handleAssignmentUpdateError}
                    onAssignmentUpdateLoading={handleAssignmentUpdateLoading}
                  />
                  {workorder.clientDetail && clientDetailsList && (
                    <Card title={t("workorders.clientDetails")} className="mb-3" loading={pageLoading}>
                      <FormContainer dataList={clientDetailsList} />
                    </Card>
                  )}
                  <Card title={t("workorders.extraDetails")} loading={pageLoading} className="mb-3">
                    <ExtraDetails
                      data={workorder.extraDetail}
                      editable={editingWorkorder}
                      onEdit={handleExtraDetailsChange}
                    />
                  </Card>
                  <div>
                    {switchgearErrorToastMessage && (
                      <MessageToast
                        text={switchgearErrorToastMessage}
                        mode={ToastMode.Error}
                        show={switchgearErrorToastMessage != null}
                      ></MessageToast>
                    )}
                  </div>
                </Col>
              </Row>
            )}
          </Container>

          {confirmCancellation && (
            <ConfirmationDialog
              message={t("workorders.actions.confirmCancel")}
              onAccept={handleCancellationConfirmation}
              onClose={cancelWorkorderCancellation}
              visible={confirmCancellation}
              header={t("workorders.actions.cancel")}
            />
          )}
          {workorder && resultDialogOpen && (
            <CompleteWorkorderDialog
              workorderId={workorder.id}
              customerId={workorder.customerId}
              open={resultDialogOpen}
              onUploadSuccess={handleResultUploadSuccess}
              onCancel={handleResultUploadCancel}
            />
          )}

          {createWorkorderDialogOpen && (
            <WorkorderCreate
              customerSelected={!!workorder?.customerId}
              open={createWorkorderDialogOpen}
              onClose={handleDuplicatWorkorderDialogClose}
              customer={
                {
                  id: workorder?.customerId,
                  name: workorder?.customerName,
                } as Customer
              }
              onWorkorderCreated={handleDuplicateWorkorderCreated}
              prepopulatedFromWorkorder={workorder}
            />
          )}

          <Snackbar
            open={updateWorkorderError || unassignmentError || updateAssignmentError}
            onClose={() => {
              setUpdateWorkorderError(false);
              setUnassignmentError(false);
              setUpdateAssignmentError(false);
            }}
          >
            <div>
              <MessageToast
                text={t("common.dataUpdateError", {
                  val: "$t(workorders.workorder)",
                })}
                mode={ToastMode.Error}
                show={true}
              />
            </div>
          </Snackbar>

          <Snackbar
            open={showSnackbar}
            onClose={() => {
              setShowSnackbar(false);
            }}
            message={snackbarText}
            actionText={snackbarAction?.text}
            onActionClick={snackbarAction?.action}
          />
        </FormProvider>
      </React.Fragment>
    );
}
